/**
 * Visitor identity & sales funnel stage tracking
 *
 * Visitor ID: generated on first visit, persisted in localStorage + 1st-party cookie
 * so it survives page reloads and can be read server-side (e.g. for attribution).
 *
 * Funnel stage: one-way progression (never goes back).
 * Used for:
 *  - GA4 User Property → GA4 Audiences → Google Ads remarketing segments
 *  - Google Ads remarketing tag custom parameter (te_funnel_stage)
 *  - Exclusion: "submitted" users are excluded from lead-gen campaigns
 */

const VID_KEY = "te_vid";
const STAGE_KEY = "te_fs";

export const FUNNEL_STAGES = [
  "visitor",
  "lead",
  "registered",
  "applicant",
  "package_selected",
  "submitted",
] as const;

export type FunnelStage = (typeof FUNNEL_STAGES)[number];

// ─── Visitor ID ───────────────────────────────────────────────────────────────

/** Get or create a persistent visitor ID (localStorage + 1st-party cookie). */
export function getOrCreateVisitorId(): string {
  try {
    let id = localStorage.getItem(VID_KEY);
    if (!id) {
      id = generateId();
      localStorage.setItem(VID_KEY, id);
      // Write to 1st-party cookie (1 year) so _worker.js / n8n can read it
      document.cookie = `${VID_KEY}=${id};max-age=31536000;path=/;SameSite=Lax`;
    }
    return id;
  } catch {
    return "te_unknown";
  }
}

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return "te_" + crypto.randomUUID().replace(/-/g, "").slice(0, 20);
  }
  return "te_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 9);
}

// ─── Funnel stage ─────────────────────────────────────────────────────────────

export function getFunnelStage(): FunnelStage {
  try {
    const stored = localStorage.getItem(STAGE_KEY) as FunnelStage | null;
    return FUNNEL_STAGES.includes(stored as FunnelStage) ? (stored as FunnelStage) : "visitor";
  } catch {
    return "visitor";
  }
}

/**
 * Advance the funnel stage — only moves forward, never backward.
 * E.g. a "submitted" user can't be downgraded back to "lead".
 */
export function advanceFunnelStage(stage: FunnelStage): void {
  try {
    const current = getFunnelStage();
    if (FUNNEL_STAGES.indexOf(stage) > FUNNEL_STAGES.indexOf(current)) {
      localStorage.setItem(STAGE_KEY, stage);
    }
  } catch {
    // localStorage unavailable (incognito, storage full) — silently ignore
  }
}
